import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox } from '..';
import { DropdownFilterOptionType } from './DropdownFilter';
import DropdownArrow from '@/assets/caretDownLight.svg';
import classNames from 'classnames';

type DropdownFilterTreeOptionsProps = {
  options: DropdownFilterOptionType[];
  value: string[];
  onChange?: (value: string) => void;
};

// Find all parent IDs that have checked descendants
const findParentsWithCheckedChildren = (opts: DropdownFilterOptionType[], checkedValues: Set<string>): Set<string> => {
  const parentIds = new Set<string>();

  const visit = (option: DropdownFilterOptionType): boolean => {
    const childHasChecked = option.options?.some(visit) ?? false;
    const selfChecked = checkedValues.has(option.value);

    if (childHasChecked && option.options?.length) {
      parentIds.add(option.value);
    }

    return selfChecked || childHasChecked;
  };

  opts.forEach(visit);
  return parentIds;
};

const hasCheckedDescendant = (option: DropdownFilterOptionType, checkedValues: Set<string>): boolean => {
  return (
    option.options?.some((child) => checkedValues.has(child.value) || hasCheckedDescendant(child, checkedValues)) ??
    false
  );
};

const renderOptions = (
  opts: DropdownFilterOptionType[],
  selectedValues: Set<string>,
  onChange: ((option: DropdownFilterOptionType) => void) | undefined,
  expandedItems: Set<string>,
  onToggleExpand: (id: string) => void,
  depth: number = 0,
): ReactElement => {
  return (
    <div className="flex flex-col gap-4">
      {opts.map((option) => {
        const hasChildren = !!option.options?.length;
        const hasSelectedChild = hasChildren && hasCheckedDescendant(option, selectedValues);
        const isExpanded = expandedItems.has(option.value);
        const indentStyle = depth > 0 ? { paddingLeft: Math.min(depth * 16, 64) } : undefined;
        return (
          <div key={option.value}>
            <div className="whitespace-nowrap" style={indentStyle}>
              <div className="flex items-center gap-2">
                {hasChildren ? (
                  <button
                    type="button"
                    onClick={() => onToggleExpand(option.value)}
                    className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full transition-colors hover:bg-gray-200"
                  >
                    <DropdownArrow
                      className={classNames('h-4 w-4 fill-current transition-transform duration-200', {
                        'rotate-0': isExpanded,
                        '-rotate-90': !isExpanded,
                      })}
                    />
                  </button>
                ) : (
                  <div className="w-5" />
                )}

                <Checkbox
                  label={option.label}
                  checked={selectedValues.has(option.value)}
                  indeterminate={hasSelectedChild}
                  onChange={() => onChange?.(option)}
                  disabled={option.disabled}
                />
              </div>
            </div>

            {hasChildren && isExpanded && (
              <div
                className={classNames(
                  'overflow-hidden transition-all duration-300 ease-in-out',
                  isExpanded ? 'mt-4 max-h-[3000px] opacity-100' : 'max-h-0 opacity-0',
                )}
              >
                {renderOptions(option.options!, selectedValues, onChange, expandedItems, onToggleExpand, depth + 1)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const DropdownFilterTreeOptions = ({ options, value, onChange }: DropdownFilterTreeOptionsProps) => {
  const selectedValues = useMemo(() => new Set(value), [value]);
  const hasInitializedExpansion = useRef(false);

  const parentsWithCheckedChildren = useMemo(
    () => findParentsWithCheckedChildren(options, selectedValues),
    [options, selectedValues],
  );

  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => new Set(parentsWithCheckedChildren));

  useEffect(() => {
    if (hasInitializedExpansion.current) {
      return;
    }

    setExpandedItems(new Set(parentsWithCheckedChildren));
    hasInitializedExpansion.current = true;
  }, [parentsWithCheckedChildren]);

  const onOptionChange = useCallback(
    (option: DropdownFilterOptionType) => {
      const hasChildren = !!option.options?.length;
      const isChecked = selectedValues.has(option.value);

      if (hasChildren && !isChecked) {
        setExpandedItems((prev) => {
          if (prev.has(option.value)) {
            return prev;
          }

          const next = new Set(prev);
          next.add(option.value);
          return next;
        });
      }

      onChange?.(option.value);
    },
    [onChange, selectedValues],
  );

  const onToggleExpand = useCallback((id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }, []);

  const renderedOptions = useMemo(
    () => renderOptions(options, selectedValues, onOptionChange, expandedItems, onToggleExpand),
    [options, selectedValues, onOptionChange, expandedItems, onToggleExpand],
  );

  return <>{renderedOptions}</>;
};

export default DropdownFilterTreeOptions;
